/// <reference path="../../../global.d.ts" />
'use client';

import { cloneElement, forwardRef, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useConstant } from '../../lib/internal/hooks';
import { getCleanLocationHref } from '../../lib/urlUtils';
import { warnOnDevIfCloneElementWillFail } from '../../lib/utils';
import { EventName, type ShoppingFinishedPayload, type SkuViewedPayload } from '../../Shibuya';
import { useAnalyticsPageView } from '../PageView/Context';
import {
  type AnalyticsShoppingFinishedCallbackProps,
  AnalyticsSkuViewedContext,
  type AnalyticsSkuViewedContextValue,
} from './Context';

export interface AnalyticsSkuViewedProps {
  id: SkuViewedPayload['sku_id'];
  source?: SkuViewedPayload['sku_source'];
  name: SkuViewedPayload['sku_name'];
  productType?: SkuViewedPayload['sku_product_type'];
  level?: SkuViewedPayload['sku_level'];
  institutionId?: SkuViewedPayload['institution_id'];
  institutionName?: SkuViewedPayload['institution_name'];
  productId?: SkuViewedPayload['product_id'];
  productName?: SkuViewedPayload['product_name'];
  offerId?: SkuViewedPayload['offer_id'];
  offerName?: SkuViewedPayload['offer_name'];
  monetizable: SkuViewedPayload['monetizable'];
  impressionSection: SkuViewedPayload['impression_section'];
  impressionPosition: SkuViewedPayload['impression_position'];
  additionalAttributes?: SkuViewedPayload['additional_attributes'];
  dangerouslySetShoppingFinishedId?: string;
  overrideSkuImpressionId?: string;
  direction?: 'horizontal' | 'vertical';
  scrollableAncestor?: HTMLElement;
  enabled?: boolean;
  children?: React.JSX.Element | ((context: AnalyticsSkuViewedContextValue) => React.JSX.Element);
}

export const AnalyticsSkuViewed = forwardRef<HTMLElement, AnalyticsSkuViewedProps>(
  (
    {
      children,
      dangerouslySetShoppingFinishedId,
      scrollableAncestor,
      overrideSkuImpressionId,
      enabled = true,
      ...props
    },
    ref,
  ) => {
    const {
      analytics,
      deployableName,
      deployableVersion,
      deviceId,
      gitSha,
      defaultSkuViewedSource,
      defaultSkuViewedProductType,
      defaultSkuViewedLevel,
    } = useAnalytics();
    const { pageViewId, pageName, pageViewReady } = useAnalyticsPageView();
    const [skuViewedPayload, setSkuViewedPayload] = useState<SkuViewedPayload>();
    const skuImpressionId = useConstant(() => crypto.randomUUID());
    const shoppingFinishedId = useConstant(() => {
      return dangerouslySetShoppingFinishedId || crypto.randomUUID().replace(/-/g, '');
    }, [dangerouslySetShoppingFinishedId]);

    const { ref: inViewRef, inView } = useInView({
      root: scrollableAncestor ? scrollableAncestor || 'window' : null,
      skip: !enabled,
      rootMargin: scrollableAncestor ? '0px' : '1px',
      threshold: 0,
      triggerOnce: true,
    });

    const setRefs = (node: HTMLElement | null) => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref && 'current' in ref) {
        ref.current = node;
      }
      inViewRef(node);
    };

    useEffect(() => {
      if (pageViewReady && deviceId) {
        const eventPayload: SkuViewedPayload = {
          sku_id: props.id,
          sku_source: props.source ?? defaultSkuViewedSource,
          sku_name: props.name,
          sku_product_type: props.productType ?? defaultSkuViewedProductType,
          sku_level: props.level ?? defaultSkuViewedLevel,
          institution_id: props.institutionId,
          institution_name: props.institutionName,
          product_id: props.productId,
          product_name: props.productName,
          offer_id: props.offerId,
          offer_name: props.offerName,
          monetizable: props.monetizable,
          impression_page_name: pageName || window.location.pathname,
          impression_page_url: getCleanLocationHref(),
          impression_pageview_id: pageViewId,
          impression_section: props.impressionSection,
          impression_position: props.impressionPosition,
          sku_impression_id: overrideSkuImpressionId ?? skuImpressionId,
          deployable_name: deployableName,
          deployable_version: deployableVersion,
          traditional_device_id: deviceId,
          additional_attributes: props.additionalAttributes,
        };

        setSkuViewedPayload(eventPayload);
      }
    }, [pageViewReady, deviceId]);

    useEffect(() => {
      if (inView && skuViewedPayload) {
        analytics?.track(EventName.SKU_VIEWED, skuViewedPayload, {
          context: {
            app: {
              name: deployableName,
              version: gitSha,
            },
          },
        });

        window.dataLayer?.push({
          event: 'SkuViewed',
          ...skuViewedPayload,
        });
      }
      // Workaround to prevent multiple sku viewed events from being fired when the page is
      // remounted by navigating back in a SPA.
      return () => {
        if (skuViewedPayload && inView) {
          setSkuViewedPayload(undefined);
        }
      };
    }, [inView, skuViewedPayload]);

    const fireShoppingFinishedEvent = ({
      destinationUrl,
      ctaType,
      finishType = 'external_application',
      monetizable = skuViewedPayload?.monetizable,
      interstitialClientBotSuspected,
      revenueAmount,
      revenueType,
      entryPointPageUrl,
      estimatedRevenue,
      overrideShoppingFinishedId,
    }: AnalyticsShoppingFinishedCallbackProps) => {
      if (skuViewedPayload) {
        const eventPayload: ShoppingFinishedPayload = {
          ...skuViewedPayload,
          monetizable: monetizable,
          destination_url: destinationUrl,
          cta_type: ctaType,
          finish_type: finishType,
          interstitial_client_bot_suspected: interstitialClientBotSuspected,
          revenue_amount: revenueAmount,
          revenue_type: revenueType,
          shopping_finish_id: overrideShoppingFinishedId ?? shoppingFinishedId,
          entry_point_page_url: entryPointPageUrl,
          ...(estimatedRevenue && { estimated_revenue: estimatedRevenue }),
        };

        analytics?.track(EventName.SHOPPING_FINISHED, eventPayload, {
          context: {
            app: {
              name: deployableName,
              version: gitSha,
            },
          },
        });
        window.dataLayer?.push({
          event: 'ShoppingFinished',
          ...eventPayload,
        });
      }
    };

    const context: AnalyticsSkuViewedContextValue = {
      skuImpressionId: overrideSkuImpressionId ?? skuImpressionId,
      shoppingFinishedId,
      skuViewedPayload,
      fireShoppingFinishedEvent,
    };

    const child = typeof children === 'function' ? children(context) : children;
    if (!child) {
      throw new Error('Missing Child');
    }

    warnOnDevIfCloneElementWillFail(child, 'AnalyticsSkuViewed');
    return (
      <AnalyticsSkuViewedContext.Provider value={context}>
        {cloneElement(child, {
          ref: setRefs,
        })}
      </AnalyticsSkuViewedContext.Provider>
    );
  },
);
